import { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FilterFilled } from '@ant-design/icons';
import { Input, Button, Checkbox, Dropdown } from 'antd';
import { cn } from '@/libs/utils';

export default function TableHeaderDropdownSearch({
  title,
  dataIndex,
  searchTerm,
  toggleDropdown,
  options,
  onChange,
  onReset,
  onClose,
  onSubmit,
  dropdownWidth = 282, // default width in pixels
  placement = 'bottomRight', // default placement strategy
  // The search box exists to make a long list navigable. Over a short one it is
  // just a field to ignore — "Origin" offers two fixed choices and still asked
  // people to search them. Hidden below the threshold, and `showSearch` lets a
  // caller decide outright either way.
  searchThreshold = 6,
  showSearch,
}) {
  const { t } = useTranslation(['tables', 'forms']);

  const values = useMemo(() => {
    return searchTerm[dataIndex] || undefined;
  }, [dataIndex, searchTerm]);

  const [searchValue, setSearchValue] = useState('');
  const [selectedValues, setSelectedValues] = useState(values || []);

  useEffect(() => {
    setSelectedValues(values || []);
  }, [values]);

  const isActiveIcon = useMemo(() => {
    return options?.some(({ value }) => values?.includes(value));
  }, [options, values]);

  const handleOnReset = () => {
    setSelectedValues([]);
    onReset(dataIndex);
  };

  const handleOnSubmit = () => {
    onSubmit(dataIndex, { [dataIndex]: selectedValues });
  };

  const isSearchable = useMemo(() => {
    if (typeof showSearch === 'boolean') return showSearch;
    return (options?.length || 0) > searchThreshold;
  }, [showSearch, options?.length, searchThreshold]);

  // A hidden box must not keep filtering: without this, shrinking the option list
  // below the threshold would leave whatever had been typed still applied, with
  // nothing on screen explaining why options were missing.
  useEffect(() => {
    if (!isSearchable && searchValue) setSearchValue('');
  }, [isSearchable, searchValue]);

  const filteredOptions = useMemo(() => {
    if (!searchValue) return options;

    const lowerSearch = searchValue.toLowerCase();
    // 🚨 A label is not always a string. An option may render a node — a name
    // with its own caption under it — and `node.toLowerCase` is not a function,
    // so one such option would take the whole dropdown down the moment somebody
    // typed. `searchLabel` is what such an option is matched by instead.
    const textOf = (option) =>
      typeof option?.label === 'string' ? option.label : (option?.searchLabel ?? '');
    const matchingOptions = options.filter((option) =>
      textOf(option).toLowerCase().includes(lowerSearch)
    );

    const selectedButMissing = (selectedValues || [])
      .filter((val) => !matchingOptions.some((opt) => opt.value === val))
      .map((val) => options.find((opt) => opt.value === val))
      .filter(Boolean);

    return [...selectedButMissing, ...matchingOptions];
  }, [searchValue, options, selectedValues]);

  return (
    <Dropdown
      placement={placement}
      open={toggleDropdown[dataIndex]}
      trigger={['click']}
      onOpenChange={(value) => onClose(dataIndex, !!value)}
      popupRender={() => (
        <div className="grid gap-3 rounded-md border-[3px] border-accent bg-white p-3 shadow-md" style={{ width: `${dropdownWidth + 24}px` }}>
          <header className="">
            <span className="font-semibold">{title}:</span>
          </header>

          <div className="grid gap-3">
            <div className="grid gap-3">
              {isSearchable && (
                <Input
                  allowClear
                  type="text"
                  value={searchValue}
                  placeholder={`${t('search', { ns: 'forms' })}...`}
                  onChange={(e) => setSearchValue(e.target.value)}
                  style={{ width: `${dropdownWidth}px` }}
                />
              )}

              <div className="relative py-2">
                <Checkbox.Group
                  className="custom-ant-checkbox-group grid max-h-[204px] gap-1 overflow-y-auto overflow-x-hidden font-medium"
                  style={{ width: `${dropdownWidth}px` }}
                  options={filteredOptions}
                  value={selectedValues}
                  onChange={(newSelected) => {
                    setSelectedValues(newSelected);
                    onChange?.(dataIndex, newSelected);
                  }}
                />
              </div>
            </div>

            <div className="grid gap-2">
              <Button
                size="small"
                type="primary"
                className="btn-primary h-8 w-full"
                onClick={handleOnSubmit}
              >
                {t('submit', { ns: 'forms' })}
              </Button>

              <Button
                size="small"
                type="default"
                className="btn-default btn-default--hover h-8 w-full"
                onClick={handleOnReset}
              >
                {t('reset', { ns: 'forms' })}
              </Button>
            </div>
          </div>
        </div>
      )}
    >
      <Button
        type="default"
        className="btn-default h-6 w-6 bg-transparent p-0 shadow-none"
        icon={
          <FilterFilled
            style={{ fontSize: '13px', color: isActiveIcon ? '#666' : '#999' }}
            className={cn(
              'rounded-full p-1.5',
              isActiveIcon ? 'bg-white text-accent shadow-md' : ''
            )}
          />
        }
      >
        <span className="sr-only">{t('filters', { ns: 'tables' })}</span>
      </Button>
    </Dropdown>
  );
}
